import { createTheme } from '@mui/material/styles';

import BlockquoteStyles from 'components/Blockquote/styles';
// import ContentBlockStyles from 'components/ContentBlock/styles';
import FilterStyles from 'components/FilterSet/styles';
import GalleryStyles from 'components/Gallery/styles';
import InlineVideoStyles from 'components/InlineVideo/styles';
import MastheadStyles from 'components/Masthead/styles';
import ZeekStyles from 'components/Zeek/styles';
import FullscreenVideoStyles from 'components/FullscreenVideo/styles';
import TimelineStyles from 'components/Timeline/styles';
import Link from 'components/Link';
import TwoColumnStyles from 'components/TwoColumn/styles';
import TwoColumnVideoStyles from 'components/TwoColumnVideo/styles';
import background404 from 'images/404-background.svg';
import blackhole from 'images/blackhole.svg';
import externalLink from 'images/icons/externalLink.svg';

// import { typography } from './baseTheme';
import breakpoints from './breakpoints';
// import theme, { typography } from './baseTheme';
import mergeDeep from './mergeDeep';


export const getColor = (name, shade = 400) => {
	// TODO: Support shade names (lighter = 100, light = 300, regular = 400, bold = 700, heavy = 900)
	// TODO: Support intermediate shade values (if there's no shade match, blend the two nearest by the shade provided)
	if (name === 'white' && typeof colors.white === 'string')return colors.white; // This is a patch because the colors.white is being overwritten
	return colors[name][shade];
};

export const remCalc = (px, base = 16) => {
	const tempPx = `${px}`.replace('px', '')

	return (1 / base) * parseInt(tempPx) + 'rem'
}

export const globalTransitionDuration = '0.25s';

export const dateFormat = {
	year: '2-digit',
	month: 'numeric',
	day: 'numeric',
};

const componentStyles = mergeDeep(
	TwoColumnStyles,
	TwoColumnVideoStyles,
	BlockquoteStyles,
	GalleryStyles,
	InlineVideoStyles,
	MastheadStyles,
	FilterStyles,
	ZeekStyles,
	FullscreenVideoStyles,
	TimelineStyles,
);

// Color Palette Update Changes -> 8

// Blue
// 1. primary 500 (#0F2345) -> Blue 700 (Oxford Blue) -> Theme.js ✅ -> Repo ✅
// 2. primary 400 (#174A87) -> Blue 500 (Polynesian Blue) -> Theme.js ✅ -> Repo ✅
// 3. secondary 200 (#A8DEEF) -> Blue 200 (Non Photo Blue) -> Theme.js ✅ -> N/A in Repo ✅

// Gray
// 4. primary 300 (#7799A8) -> Gray 600 (Cadet Gray) -> Theme.js ✅ -> Repo ✅

// Red
// 5. accent1 200 (#F05644) -> Red 500 (Tomato) -> Theme.js ✅ -> Repo ✅

// Yellow
// 6. accent1 100 (#FFCE64) -> Yellow 600 (Sunglow) -> Theme.js ✅ -> Repo ✅

// No Matches
// 7. secondary 300 (#4C7FBE) -> proposed change: Blue 300 -> Theme.js ✅ -> N/A in Repo ✅
// 8. secondary 100 (#F3E3D1) -> proposed change: Khaki 700 -> Theme.js ✅ -> N/A in Repo ✅

const colors = {
	primary: {
		500: '#0F2345', // Blue 700
		400: '#174A87', // Blue 500
		300: '#7799A8', // Gray 600
	},
	secondary: {
		300: '#4C7FBE', // No Direct Match in new palette
		200: '#A8DEEF', // Blue 200
		100: '#F3E3D1', // No Direct Match in new palette
	},
	accent1: {
		200: '#F05644', // Red 500
		100: '#FFCE64', // Yellow 600
	},
	white: {
		100: '#FFFFFF',
	},
	blue: {
		700: '#0F2345',
		600: '#045172',
		500: '#174A87',
		400: '#23799E',
		300: '#348EB2',
		200: '#A8DEEF',
		100: '#B7E0EA',
	},
	gray: {
		700: '#979797',
		600: '#7799A8',
		500: '#BCCCD0',
	},
	khaki: {
		700: '#EDD9C3',
	},
	red: {
		700: '#A41D13',
		600: '#EF3426',
		500: '#F05644',
	},
	yellow: {
		700: '#FFBD52',
		600: '#FFCE64',
	}
};
colors.white = getColor('white', 100);
colors.black = '#000000';

let theme = createTheme({
	breakpoints: breakpoints,
});

const writeBackgroundClasses = (keys = ['blue', 'gray', 'khaki', 'red', 'yellow']) => {
	const classes = {};
	for (let key of keys) {
		if (colors[key] === undefined)continue;
		for (let shade in colors[key]) {
			classes[`.bg_${key}_${shade}`] = {
				backgroundColor: colors[key][shade],
			}
		}
	}
	return classes;
}

const statCardStyles = {
	backgroundColor: getColor('gray', 600),
	borderRadius: 0,
	height: '100%',
	[theme.breakpoints.down('sm')]: {
		minHeight: '200px',
	},
	'.cardActionArea': {
		height: '100%',
		borderRadius: 0,
		borderBottom: '#F05644 0.5rem solid',
		padding: '40px',
		textAlign: 'center',
		color: getColor('blue',700),
		transition: `all ${globalTransitionDuration} ease-in-out`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: getColor('gray', 600),
		'.MuiCardContent-root:last-child': {
			paddingBottom: '0px',
		},
		'&:hover:not(.noHover)': {
			backgroundColor: getColor('blue', 500),
		},
		'img': {
			marginBottom: remCalc(25),
		},
		'.stat': { // Title 
			color: colors.white,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			fontSize: 40,
			fontStyle: 'normal',
			fontWeight: 700,
			lineHeight: 1.15,
		},
		'.accolade': { // Summary
			color: colors.white,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: 36,
			[theme.breakpoints.down('sm')]: {
				fontSize: 28,
				marginBottom: remCalc(18),
			},
			fontStyle: 'normal',
			fontWeight: 600,
			lineHeight: 1.167,
			marginBottom: remCalc(36),
		},
		'.info': { // Description
			color: colors.white,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: 20,
			[theme.breakpoints.down('sm')]: {
				fontSize: 16,
				marginBottom: remCalc(24),
			},
			fontStyle: 'normal',
			fontWeight: 600,
			lineHeight: 1.167,
			// marginBottom: remCalc(48),
		},
		'.card__topics__container': {
			display: 'flex',
			gap: remCalc(5),
			marginBottom: remCalc(18),
			flexFlow: 'wrap'
		}
	}
}

const getArticleCardStyles = (inverse = false) => {
	const baseBackgroundColor = inverse ? getColor('blue', 500) : getColor('gray', 600);
	const hoverBackgroundColor = inverse ? getColor('gray', 600) : getColor('blue', 500);

	return {
		backgroundColor: baseBackgroundColor,
		borderRadius: 11,
		height: '100%',
		transition: 'all 200ms ease-in-out',
		'&:hover': {
			borderRadius: '11px 64px',
		},
		'.MuiCardActionArea-root': {
			backgroundColor: baseBackgroundColor,
			transition: 'all 200ms linear',
			height: '100%',
			display: 'flex',
			justifyContent: 'flex-start',
			flexDirection: 'column',
			'&:hover': {
				backgroundColor: hoverBackgroundColor,
			},
		},
		'.MuiCardMedia-root': {
			borderRadius: 11,
		},
		'.articleCard__image': {
			width: '100%',
			'.Image-figure': {
				width: '100%',
				[theme.breakpoints.down('sm')]: {
					maxHeight: 'unset',
				},
			},
		},
		'.MuiCardContent-root': {
			height: '100%',
			borderRadius: 11,
			padding: '29px 60px 30px 27px',
			textAlign: 'left',
			color: getColor('white'),
			display: 'flex',
			alignItems: 'flex-start',
			justifyContent: 'space-between',
			flexDirection: 'column',
			'&.hasIcon': {
				padding: '30px',
			},
			'h3': {
				color: getColor('white'),
				fontFamily: [
					'din-2014',
					'sans-serif',
				].join(', '),
				fontSize: 25,
				fontStyle: 'normal',
				fontWeight: 600,
				lineHeight: 1.167,
				marginBottom: remCalc(10),
			},
			'.MuiTypography-p__card__date': {
				marginBottom: remCalc(10),
			},
			'.card__topics__container': {
				display: 'flex',
				gap: remCalc(5),
				marginTop: remCalc(18),
				flexFlow: 'wrap',
				flex: 0,
			},
			'.card__content__container': {
				display: 'flex',
				flexDirection: 'row',
				gap: remCalc(24),
				'.card__content__container__top': {
					flex: '1',
				},
				'.roundImage': {
					width: remCalc(70),
					height: remCalc(70),
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'none',
					flex: '0 auto',
					borderRadius: '100%',
					[theme.breakpoints.up('md')]: {
						width: remCalc(168),
						height: remCalc(168),
					},
				}
			}
		}
	};
}

const typography = {
	typography: {
		htmlFontSize: 10,
		fontSize: 16,
		fontFamily: [
			'Open Sans',
			'sans-serif',
		].join(', '),
		// Headers
		h1: {
			color: colors.white,
			fontSize: remCalc(30),
			lineHeight: 1.06,
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(48),
				lineHeight: 1.08,
			},
			'&.slim': {
				fontWeight: 400,
				lineHeight: 1.15,
			},
			'&.h1Z30': {
				fontWeight: 400,
				textAlign: 'center',
				lineHeight: '1.15',
				textTransform: 'uppercase',
				[theme.breakpoints.up('md')]: {
					fontSize: remCalc(65),
				},
				strong: {
					fontWeight: 600,
					display: 'block',
					fontSize: remCalc(55),
					[theme.breakpoints.up('md')]: {
						fontSize: remCalc(100),
					},
				},
			},
			//Doesn't work :(
			//variants: [
			//	{
			//		props: { variant: 'large' },
			//		style: {
			//			fontSize: remCalc(40),
			//			lineHeight: 1.15,
			//			[theme.breakpoints.up('md')]: {
			//				fontSize: remCalc(75),
			//				lineHeight: 1.06,
			//			}
			//		}
			//	}
			//],
		},
		h1_Large: {
			fontSize: remCalc(50),
			fontWeight: 'bold',
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(65),
			},
		},
		h1MastheadTitle: {
			fontSize: remCalc(40),
			lineHeight: 1.15,
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(65),
				lineHeight: 1.06,
			}
		},
		h1__404: {
			color: colors.white,
			fontSize: remCalc(40),
			lineHeight: remCalc(46),
			marginBottom: remCalc(9),
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(96),
				lineHeight: remCalc(75),
				marginBottom: remCalc(30),
			},
		},
		h2: {
			color: colors.white,
			fontSize: remCalc(28),
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			lineHeight: 1.07,
			textTransform: 'none',
			fontWeight: 600,
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(40),
				lineHeight: 1.15,
			},
		},
		h2CardTitle: {
			color: colors.white,
			fontSize: remCalc(28),
			lineHeight: 1.07,
			textTransform: 'none',
			fontWeight: 400,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(40),
                lineHeight: 1.15,
            },
		},
		h2Result: {
			color: colors.white,
			margin: 0,
			fontSize: remCalc(64),
			lineHeight: .83,
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
		},
		h3: {
			color: colors.white,
			fontSize: remCalc(28),
			lineHeight: 1.07,
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(36),
				lineHeight: 1.11,
			},
			//variants: [
			//	{
			//		props: { variant: 'card' },
			//		style: {
			//			fontWeight: 400,
			//			fontFamily: [
			//				'din-2014',
			//				'sans-serif',
			//			].join(', '),
			//		}
			//	}
			//]
		},
		h3__card: {
			color: colors.white,
			fontSize: remCalc(28),
			lineHeight: 1.07,
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(36),
                lineHeight: 1.11,
            },
		},
		h4: {
			color: colors.white,
			fontSize: remCalc(24),
			lineHeight: 1.16,
			textTransform: 'none',
			fontWeight: 600,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			variants: [
				{
					props: { variant: 'card' },
					style: {
						fontWeight: 400,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
					}
				}
			]
		},
		h5: {
			color: colors.white,
			fontSize: remCalc(20),
			lineHeight: 1.2,
			textTransform: 'none',
			fontWeight: 400,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
		},
		h6: {
			color: colors.white,
			fontSize: remCalc(18),
			lineHeight: 1.33,
			textTransform: 'none',
			fontWeight: 400,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
		},
		// Body
		body1: {
			color: colors.white,
			fontSize: remCalc(16),
			lineHeight: 1.25,
			fontWeight: 400,
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(20),
				lineHeight: 1.2,
			},
			a: {
				color: colors.white,
                textDecoration: 'underline',
				transition: `all ${globalTransitionDuration} linear`,
				'&:not(.MuiButton-root):hover': {
					color: getColor('yellow', 600),
				}
			}
		},
		body1Masthead: {
			color: colors.white,
            fontSize: remCalc(20),
            lineHeight: 1.4,
            fontWeight: 400,
            [theme.breakpoints.up('md')]: {
                fontSize: remCalc(24),
                lineHeight: 1.16,
            }
		},
		body1MastheadSmall: {
			color: colors.white,
            fontSize: remCalc(16),
            lineHeight: 1.25,
            fontWeight: 400,
			[theme.breakpoints.up('md')]: {
                fontSize: remCalc(20),
                lineHeight: 1.2,
            }
		},
		body1ResultSubject: {
			color: colors.white,
			fontSize: remCalc(28),
			lineHeight: 1.64,
			fontWeight: 600,
			marginBottom: remCalc(14),
			
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(36),
                lineHeight: 1.27,
				fontWeight: 600,
				marginBottom: remCalc(20),
            }
		},
		body1ResultCopy: {
			color: colors.white,
			fontSize: remCalc(16),
			lineHeight: 1.25,
			fontWeight: 400,
			fontFamily: [
				'open-sans',
				'sans-serif',
			].join(', '),
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(20),
				lineHeight: 1.20,
			}
		},
		// Generated by Copilot
		body2: {
			color: colors.white,
			fontSize: remCalc(14),
			lineHeight: 1.5,
			fontWeight: 400,
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(18),
				lineHeight: 1.25,
			}
		},
		legal: {
			fontFamily: [
				'open-sans',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(12),
			fontWeight: 400,
			color: getColor('gray', 600),
		},
		// End
		p__card__date: {
			fontSize: remCalc(14),
			lineHeight: 1.5,
			fontWeight: 400,
			fontStyle: 'italic',
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(18),
				lineHeight: 1.875,
			}
		},
		p__card: {
			fontSize: remCalc(16),
            lineHeight: 1.25,
            fontWeight: 400,
			[theme.breakpoints.up('md')]: {
                fontSize: remCalc(20),
                lineHeight: 1.2,
				'&.staffCard__tenure': {
					fontSize: '1rem',
					marginTop: '12px'
				}
            }
		},
		p__blog__post__masthead__date: {
			fontSize: remCalc(16),
            lineHeight: 1.25,
            fontWeight: 400,
            fontStyle: 'italic',
			[theme.breakpoints.up('md')]: {
                fontSize: remCalc(20),
                lineHeight: 1.2,
            }
		},
		p__blog__post__masthead__intro: {
			fontSize: remCalc(14),
            lineHeight: 1.25,
            fontWeight: 400,
			[theme.breakpoints.up('md')]: {
                fontSize: remCalc(15),
            }
		},
		p__blog__post__masthead__backLink: {
			fontSize: remCalc(16),
            lineHeight: 1.25,
            fontWeight: 400,
            color: colors.white,
            textDecoration: 'none',
            marginBottom: '80px',
			[theme.breakpoints.up('md')]: {
                fontSize: remCalc(20),
                lineHeight: 1.2,
            },
            'a': {
            	color: colors.white,
				'.back__arrow': {
	            	transform: 'rotate(90deg) translateX(10px)',
	            },
            }
		},
		nav: {
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(16),
			lineHeight: 1,
			fontWeight: 600,
			textTransform: 'uppercase',
			[theme.breakpoints.up('md')]: {
				fontSize: remCalc(20),
			},
		},
		mobileNav: {
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(30),
			color: getColor('white', 100),
			lineHeight: 1,
			fontWeight: 600,
			textTransform: 'uppercase',
		},
		footerLinkParent: {
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(20),
			fontWeight: 600,
			lineHeight: 1,
			textTransform: 'uppercase',
		},
		footerLinkChild: {
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(20),
			fontWeight: 600,
			lineHeight: 1,
		},
		footerLocation: {
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(25),
			fontWeight: 600,
			lineHeight: 1.25,
		},
		copyright: {
			fontFamily: [
				'open-sans',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(16),
			fontWeight: 600,
			color: getColor('gray', 600),
		},
		awardsTitle: {
			color: colors.white,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(40),
			fontStyle: "normal",
			fontWeight: 600,
			lineHeight: "46px",
			textTransform: "none",
			[theme.breakpoints.down('md')]: {
				fontSize: remCalc(30),
                lineHeight: "53px",
            },
            [theme.breakpoints.down('sm')]: {
            	lineHeight: 1.25
            }
		},
		awardsText: {
			color: colors.white,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(24),
			fontStyle: "normal",
			fontWeight: 600,
			lineHeight: "34px",
			textTransform: "none",
			[theme.breakpoints.down('md')]: {
				fontSize: remCalc(20),
            },
		},
		formTitle: {
			color: colors.white,
			fontFamily: [
				'franklin-gothic-atf',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(48),
			fontStyle: "normal",
			fontWeight: 600,
			lineHeight: "46px",
			marginBottom: "2rem",
			textTransform: "none",
			[theme.breakpoints.down('md')]: {
				fontSize: remCalc(30),
                lineHeight: 1.25,
            },
		},
		formText: {
			color: colors.white,
			fontFamily: [
				'open-sans',
				'sans-serif',
			].join(', '),
			fontSize: "1.25rem",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "24px",
			marginBottom: "4rem",
			textTransform: "none",
			[theme.breakpoints.down('md')]: {
				fontSize: remCalc(16),
                lineHeight: "20px",
            },
		},
		staffCategories: {
			color: colors.white,
			fontFamily: [
				'din-2014',
				'sans-serif',
			].join(', '),
			fontSize: remCalc(24),
			fontStyle: "normal",
			fontWeight: 600,
			lineHeight: "38px",
			textTransform: "none",
		},
	},	
}

theme = createTheme(mergeDeep({
	breakpoints: {
		values: {
			...theme.breakpoints.values,
			phone: theme.breakpoints.values.xs,
			tablet: theme.breakpoints.values.sm,
			laptop: theme.breakpoints.values.md,
			desktop: theme.breakpoints.values.lg,
			// nav: theme.breakpoints.values.xl,
		},
	},
	...typography,
	palette: {
		primary: {
			main: getColor('blue', 700),
			dark: getColor('blue', 700),
			light: getColor('gray', 600),
		},
		secondary: {
			main: getColor('blue', 300),
		},
		background: {
			default: getColor('blue', 700),
		},
		accent1: {
			main: getColor('red', 500),
		},
		white: {
			main: colors.white,
		},
		text: {
			primary: colors.white,
		}
	},
	components: {
		MuiGrid2: {
			defaultProps: {
				spacing: {
					phone: 1,
					tablet: 2,
					laptop: 4,
				},
				columnSpacing: {
					phone: 1,
					tablet: 2,
					laptop: 4,
				},
				rowSpacing: {
					phone: 1,
					tablet: 2,
					laptop: 4,
				}
			},
			styleOverrides: {
				root: {
					'img.content__img': {
						maxWidth: '100%',
						height: 'auto',
					},
					'&.filters__container': {
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						zIndex: 1
					},
					'&.filters__container__spacer': {
						height: remCalc(74),
						marginBottom: remCalc(36),
					},
					'&.insights__list__results': {
						fontSize: remCalc(18),
						fontStyle: 'normal',
						fontWeight: 700,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: remCalc(20.95),
					},
					'&.insights__list__filters__text__wrapper': {
						display: 'flex',
						gap: remCalc(80),
						marginBottom: remCalc(18),
						'.insights__list__filters__text': {
							fontSize: remCalc(16),
							fontStyle: 'normal',
							fontWeight: 700,
							fontFamily: [
								'din-2014',
								'sans-serif',
							].join(', '),
							lineHeight: remCalc(18.63),
							display: 'flex',
							alignItems: 'center',
							gap: remCalc(9),
						}
					},
					'&.single_location__description_wrapper' : {
						borderRadius: '0px 50px',
						backgroundColor: getColor('blue',700),
						padding: '37px 32px 47px 32px',
						[theme.breakpoints.up('md')]: {
							padding: '47px 63px 57px 73px',
						},
						'h3': {
							fontFamily: [
								'din-2014',
								'sans-serif',
							].join(', '),
							fontSize: remCalc(40),
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: remCalc(46),
							marginBottom: remCalc(29),
						},
						'p': {
							fontFamily: [
								'open-sans',
								'sans-serif',
							].join(', '),
							fontSize: remCalc(20),
							[theme.breakpoints.down('sm')]: {
								fontSize: 16,
								marginBottom: remCalc(24),
							},
							fontStyle: 'normal',
							fontWeight: 400,
							lineHeight: remCalc(24),
							marginBottom: remCalc(20),
							'&.phone, &.email': {
								marginBottom: 0,
							}
						}
					},
					'&.single_location__directions': {
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						'h3': {
							marginBottom: remCalc(27),
						},
						'p': {
							marginBottom: remCalc(52),
						},
						[theme.breakpoints.down('sm')]: {
							textAlign: 'center',
							paddingLeft: remCalc(15),
							paddingRight: remCalc(15),
						}
					},
					'&.single_location__directions__submarine_container': {
						paddingLeft: remCalc(32),
						[theme.breakpoints.down('sm')]: {
							textAlign: 'center',
						}
					},
					'&.contact_form__grid': {
						'.contact_form__wrapper': {
							backgroundColor: getColor('blue', 500),
							padding: '52px 62px',
							borderRadius: remCalc(11),
							'.contact_form__submit__wrapper': {
								textAlign: 'center',
							}
						}
					}
				}
			}
		},
		MuiUseMediaQuery: {
			defaultProps: {
				noSsr: true,
			},
		},
		MuiCardActionArea: {
			defaultProps: {
				component: Link,
			}
		},
		MuiButton: {
			defaultProps: {
				component: Link,
				variant: 'contained',
				color: 'yellow',
				disableElevation: true,
				size: 'large',
			},
			variants: [
				{
					props: { variant: 'contained' },
					style: {
						fontStyle: 'normal',
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: 1.2,
						textAlign: 'center',
						transition: `all ${globalTransitionDuration} linear`,
					},
				},
				{
					props: { variant: 'contained', size: 'large' },
					style: {
						fontSize: remCalc(22),
						[theme.breakpoints.up('sm')]: {
							fontSize: remCalc(25),
						},
						fontWeight: 600,
						borderRadius: remCalc(62),
						padding: '15px 40px',
					},
				},
				{
					props: { variant: 'contained', size: 'medium' },
					style: {
						fontSize: remCalc(20),
						fontWeight: 600,
						borderRadius: remCalc(8),
						padding: '9px 20px',
						textTransform: 'none',
					},
				},
				{
					props: { variant: 'contained', size: 'small' },
					style: {
						fontSize: remCalc(12),
						fontWeight: 600,
						borderRadius: remCalc(8),
						padding: '6px 15px',
						textTransform: 'none',
					},
				},
				{
					props: { variant: 'contained', color: 'yellow' },
					style: {
						backgroundColor: getColor('yellow', 600),
						color: getColor('blue', 700),
						'&:hover': {
							backgroundColor: getColor('yellow', 700),
							borderRadius: '0 32px',
						},
					},
				},
				{
					props: { variant: 'contained', color: 'gray' },
					style: {
						backgroundColor: getColor('gray', 600),
						color: colors.white,
						'&:hover': {
							backgroundColor: getColor('yellow', 600),
						},
					},
				},
				{
					props: { variant: 'filter' },
					style: {
						fontStyle: 'normal',
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: 1.2,
						textAlign: 'center',
						transition: `all ${globalTransitionDuration} linear`,
						fontSize: remCalc(20),
						fontWeight: 600,
						borderRadius: remCalc(8),
						padding: '7px 20px',
						textTransform: 'none',
						backgroundColor: getColor('gray', 600),
						color: colors.white,
						border: `2px solid ${getColor('gray', 600)}`,
						'&:hover': {
							borderColor: getColor('blue', 200),
						},
					},
				},
				{
					props: { variant: 'filter', disable: "true" },
					style: {
						cursor: 'default',
						opacity: 0.25,
					}
				},
				{
					props: { variant: 'filter', active: "true" },
					style: {
						backgroundColor: getColor('red', 500),
						borderColor: getColor('red', 500),
					},
				},
				{
					props: { variant: 'filter', type: 'news' },
					style: {
						padding: '9px 20px',
						border: 'none',
						'&:hover': {
							backgroundColor: getColor('red', 500),
						},
					},
				},
				{
					props: { variant: 'form' },
					style: {
						background: 'transparent',
						border: '1px solid #FFF',
						borderRadius: "26px",
						fontSize: "25px",
						fontStyle: 'normal',
						fontWeight: 600,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						height: '60px',
						lineHeight: 1,
						maxWidth: '450px',
						padding: '20px 50px',
						textAlign: 'center',
						textTransform: 'uppercase',
						transition: 'all 200ms linear',
						'&:hover': {
							borderColor: getColor('gray', 600),
							backgroundColor: getColor('gray', 600),
							borderRadius: '0 32px',
						},
						// [theme.breakpoints.down('md')]: {
						// padding: "1rem 2.75rem",
						// }
					},
				},
				{
					props: { variant: 'outlined', color: 'white' },
					style: {
						fontSize: 25,
						fontStyle: 'normal',
						fontWeight: 600,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: .8,
						borderRadius: 62,
						border: `3px solid ${colors.white}`,
						padding: '17px 37px',
						textAlign: 'center',
						transition: 'all 200ms linear',
						'&:hover': {
							borderColor: getColor('gray', 600),
							backgroundColor: getColor('gray', 600),
							borderRadius: '0 32px',
						},
					}
				},
				{ 
					props: { variant: 'invisible' },
					style: {
						background: 'none',
						padding: 0,

					}
				}
			],
			styleOverrides: {
				root: ({ theme, ownerState }) => ({
					...(ownerState.external === true && {
						'&:after': {
							content: `url('${externalLink}')`,
							display: 'inline-block',
							marginLeft: remCalc(10),
						},
					}),
				}),
			},
		},
		MuiCssBaseline: {
			styleOverrides: {
				':root': {
					'--theme-color-yellow': getColor('yellow', 600),
					'--theme-color-white': getColor('white', 100),
					'--theme-color-gray': getColor('gray', 600),
					'--theme-color-primary': getColor('blue', 700),
					'--swiper-theme-color': 'white',
					'--global-transition-speed': globalTransitionDuration,
					'--theme-color-blue-600': getColor('blue', 600),
					'--swiper-pagination-top': '125px',
					[theme.breakpoints.up('sm')]: {
						'--swiper-pagination-top': '200px',
					},
					overflowX: 'hidden',
				},
				'h1, h2, h3, h4, h5, h6': {
					fontFamily: [
						'franklin-gothic-atf',
						'sans-serif',
					].join(', '),
					fontWeight: 600,
				},
				'.bg_white': {
					backgroundColor: colors.white,
				},
				...writeBackgroundClasses(),
			}
		},
		MuiPaper: {
			variants: [
				{
					props: { variant: 'label' },
					style: {
						width: 'fit-content',
						backgroundColor: getColor('gray', 600),
						color: getColor('white', 100),
						fontSize: 12,
						fontStyle: 'normal',
						fontWeight: 600,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: 1,
						borderRadius: 8,
						padding: '3px 15px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-start',
						gap: remCalc(4.7),
						border: `1px solid ${getColor('gray', 600)}`,
						cursor: 'default',
					}
				},
				{
					props: { variant: 'label', elevation: 0 },
                    style: {
						cursor: 'default',
						"&:hover, &.active": {
							borderColor: `${getColor('gray', 600)} !important`,
						}
                    }
				},
				{
					props: { variant: 'label', size: 'sm' },
					style: {
						fontSize: 5,
					}
				},
				{
					props: { variant: 'label', size: 'md' },
					style: {
						fontSize: 12,
					}
				},
				{
					props: { variant: 'label', size: 'lg' },
					style: {
						backgroundColor: getColor('gray', 600),
						color: getColor('white', 100),
						fontSize: 20,
						fontStyle: 'normal',
						fontWeight: 600,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: 1.3334,
						borderRadius: 11,
						border: `3px solid ${getColor('gray', 600)}`,
						padding: '12px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-start',
						gap: remCalc(4.7),
						transition: `all ${globalTransitionDuration} ease-in-out`,
						"&:hover:not(.ghost), &.active": {
							borderColor: getColor('blue', 200),
						},
						"&.ghost": {
							cursor: 'default',
						}
					}
				},
				{
					props: { variant: 'label', color: 'gray' },
					style: {
						backgroundColor: getColor('gray', 600),
						borderColor: getColor('gray', 600),
					}
				},
				{
					props: { variant: 'label', color: 'blue' },
					style: {
						backgroundColor: getColor('blue', 300),
						borderColor: getColor('blue', 300),
					}
				},
				{
					props: { variant: 'label', color: 'darkblue' },
					style: {
						backgroundColor: getColor('blue', 500),
						borderColor: getColor('blue', 500),
					}
				},
				{
					props: { variant: 'label', color: 'grayblue' },
					style: {
						backgroundColor: getColor('blue', 600),
						borderColor: getColor('blue', 600),
					}
				},
				{
					props: { variant: 'label', color: 'lightblue' },
					style: {
						backgroundColor: getColor('blue', 200),
						borderColor: getColor('blue', 200),
						color: getColor('blue', 700),
					}
				},
				{
					props: { variant: 'label', color: 'accent1' },
					style: {
						backgroundColor: getColor('red', 500),
						borderColor: getColor('red', 500),
					}
				},
				{
					props: { variant: 'label', color: 'red' },
					style: {
						backgroundColor: getColor('red', 500),
						borderColor: getColor('red', 500),
					}
				},
				{
					props: { variant: 'label', color: 'yellow' },
					style: {
						backgroundColor: getColor('yellow', 600),
						borderColor: getColor('yellow', 600),
						color: getColor('blue', 700),
					}
				},
				{
					props: { variant: 'label', color: 'khaki' },
					style: {
						backgroundColor: getColor('khaki', 700),
						borderColor: getColor('khaki', 700),
						color: getColor('blue', 700),
					}
				},
				{
					props: { variant: 'articleCard' },
					style: getArticleCardStyles(),
				},
				{
					props: { variant: 'articleCard', inverse: true },
					style: getArticleCardStyles(true),
				},
				{
					props: { variant: 'filter__wrapper', elevation: 0 },
					style: {
						backgroundColor: getColor('blue', 200),
						color: getColor('blue', 700),
						border: 'solid 3px '+getColor('blue', 200),
						borderRadius: 25,
						padding: '14px 24px',
						display: 'flex',
						alignItems: 'flex-start',
						justifyContent: 'flex-start',
						transition: 'all .2s ease-in-out',
						minHeight: '74px',
						position: 'relative',
						'.filter__clear': {
							position: 'absolute',
							top: remCalc(-5),
							right: remCalc(-5),
							backgroundColor: colors.white,
							borderRadius: '100%',
							padding: remCalc(1),
							transition: 'all .2s ease-in-out',
							color: getColor('blue', 700),
							'&:hover': {
								color: colors.white,
								backgroundColor: getColor('red', 500),
							}
						},
						'.filter__content': {
							display: 'none',
							alignItems: 'center',
							justifyContent: 'space-between',
							flex: '1 0 auto',
						},
						'.filter__title': {
							display: 'none',
							fontFamily: [
								'din-2014',
								'sans-serif',
							].join(', '),
							fontSize: 36,
							fontStyle: 'normal',
							fontWeight: 600,
							lineHeight: '20px',
							marginLeft: remCalc(30),
							'&.search__filter': {
								margin: 0,
								flex: '1 0 auto',
								padding: 0,
								height: 'unset',
								backgroundColor: 'transparent',
								boxShadow: 'none',
								border: 'none',
								fontFamily: [
									'din-2014',
									'sans-serif',
								].join(', '),
								fontSize: 36,
								fontStyle: 'normal',
								fontWeight: 600,
								lineHeight: remCalc(40),
								color: colors.white,
								'::placeholder': {
									color: colors.white,
									transition: 'all .2s ease-in-out',
								},
								'&:focus-visible': {
									outline: 'none',
									'::placeholder': {
										color: getColor('gray', 600),
									},
								}
							}
						},
						'svg': {
							'&:hover': {
								cursor: 'pointer',
							},
							'&.noHover': {
								'&:hover': {
									cursor: 'default',
								}
							}
						}
					}
				},
				{
					props: { variant: 'filter__wrapper', expanded: "true" },
					style: {
						backgroundColor: getColor('blue', 700),
						color: colors.white,
						flex: '1 0 auto',
						'.filter__title': {
							display: 'unset',
						},
						'.filter__content': {
							display: 'flex',
						}
					}
				},
				{
					props: { variant: 'statCard' },
					style: {
						...statCardStyles,
					}
				},
				{
					props: { variant: 'statCard--dark' },
					style: mergeDeep(statCardStyles, {
						backgroundColor: getColor('blue', 700),
						'.cardActionArea': {
							'.MuiCardContent-root:last-child': {
								paddingBottom: 0,
							},
							backgroundColor: getColor('blue', 700),
							'&:hover:not(.noHover)': {
								backgroundColor: getColor('gray', 600),
							},
							padding: remCalc(20),
							justifyContent: 'center',
							alignItems: 'center',
							'.accolade': {
								margin: '8px 0 0',
							},
						}
					})
				},
				{
					props: { variant: 'statCard--blue-500' },
					style: mergeDeep(statCardStyles, {
						backgroundColor: getColor('blue', 500),
						'.cardActionArea': {
							backgroundColor: getColor('blue', 500),
							'&:hover:not(.noHover)': {
								backgroundColor: getColor('gray', 600),
							},
						},
					}),
				},
				{
					props: { variant: 'staffCard' },
					style: {
						backgroundColor: getColor('khaki', 700),
						borderRadius: 11,
						height: '100%',
						[theme.breakpoints.down('md')]: {
						width: "350px",
						justifySelf: "center",
						},
						'.MuiCardActionArea-root': {
							backgroundColor: getColor('khaki', 700),
							transition: 'all 200ms linear',
							height: '100%',
						},
						'.MuiCardMedia-root': {
							borderRadius: 11,
						},
						'.MuiCardContent-root': {
							height: '100%',
							borderRadius: 11,
							padding: '1.125rem 1.125rem 24px',
							textAlign: 'left',
							color: getColor('blue',700),
							display: 'flex',
							alignItems: 'flex-start',
							justifyContent: 'flex-start',
							flexDirection: 'column',
							'h3': {
								color: getColor('blue',700),
							},
							'p': {
								color: getColor('blue',700),
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
							},
							'.MuiTypography-p__card__date': {
								marginBottom: remCalc(10),
							},
							'.card__topics__container': {
								display: 'flex',
								gap: remCalc(5),
								marginBottom: remCalc(18),
								flexFlow: 'wrap',
							}
						}
					}
				},
				{
					props: { variant: 'awardCard' },
					style: {
						// backgroundColor: getColor('khaki', 700),
						// borderRadius: 11,
						height: '100%',
						'.MuiCardActionArea-root': {
							// backgroundColor: getColor('khaki', 700),
							// transition: 'all 200ms linear',
							height: '100%',
							// '&:hover': {
							// 	backgroundColor: getColor('blue', 200),
							// },
						},
						'.MuiCardMedia-root': {
							// borderRadius: 11,
						},
						'.MuiCardContent-root': {
							height: '100%',
							// borderRadius: 11,
							// padding: '29px 60px 60px 27px',
							textAlign: 'left',
							color: getColor('blue',700),
							display: 'flex',
							alignItems: 'flex-start',
							justifyContent: 'flex-start',
							flexDirection: 'column',
							'h3': {
								color: getColor('blue',700),
								fontFamily: [
									'din-2014',
									'sans-serif',
								].join(', '),
								fontSize: 25,
								fontStyle: 'normal',
								fontWeight: 600,
								lineHeight: 1.167,
								marginBottom: remCalc(10),
							},
							'.MuiTypography-p__card__date': {
								marginBottom: remCalc(10),
							},
							'.card__topics__container': {
								display: 'flex',
								gap: remCalc(5),
								marginBottom: remCalc(18),
								flexFlow: 'wrap',
							}
						}
					}
				},
				{
					props: { variant: 'contactCard' },
					style: {
						backgroundColor: getColor('blue', 400),
						borderRadius: 11,
						height: '100%',
						transition: `all ${globalTransitionDuration} ease-in-out`, 
						'&.hasLink': {
							'&:hover': {
									backgroundColor: getColor('blue', 200),
									borderRadius: '0px 50px'
							},
						},
						'.MuiCardActionArea-root': {
							backgroundColor: getColor('blue', 400),
							// transition: 'all 200ms linear',
							height: '100%',
							display: 'flex',
							justifyContent: 'flex-start',
							flexDirection: 'column',
							'&:hover': {
								backgroundColor: getColor('blue', 200),
							},
						},
						'.MuiCardMedia-root': {
							borderRadius: 11,
						},
						'.MuiCardContent-root': {
							height: '100%',
							borderRadius: 11,
							padding: '40px 44px 40px',
							textAlign: 'left',
							color: getColor('white'),
							display: 'flex',
							alignItems: 'flex-start',
							justifyContent: 'space-between',
							flexDirection: 'column',
							'&.hasIcon': {
								padding: '30px',
							},
							'.contactTitle': {
								textAlign: 'center',
								color: getColor("white"),
								fontFamily: [
									'din-2014',
									'sans-serif',
								].join(', '),
								fontSize: remCalc(36),
								fontStyle: "normal",
								fontWeight: 600,
								lineHeight: "46px",
								marginBottom: remCalc(0),
							},
							'.contactText': {
								color: getColor("white"),
								lineHeight: remCalc(30),
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
								fontSize: remCalc(20),
								fontStyle: "normal",
								fontWeight: 400,
								textAlign: 'center',
								marginBottom: remCalc(34),
							},
							'.contactPhone': {
								color: getColor("white"),
								lineHeight: "30px",
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
								fontSize: remCalc(24),
								fontStyle: "normal",
								fontWeight: 400,
								textAlign: 'center',
								marginBottom: remCalc(6),
								'a': {
									color: getColor("white"),
									textDecoration: 'none',
									[theme.breakpoints.up('md')]: {
										'&:hover': {
											textDecoration: 'underline',
											// color: getColor("blue", 400),
										}
									}
								}
							},
							'.contactEmail': {
								color: getColor("blue", 700),
								lineHeight: "30px",
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
								fontSize: remCalc(24),
								fontStyle: "normal",
								fontWeight: 400,
								textAlign: 'center',
								'a': {
									color: getColor("white"),
									textDecoration: 'none',
									[theme.breakpoints.up('md')]: {
										'&:hover': {
											textDecoration: 'underline',
											// color: getColor("blue", 400),
										}
									}
								}
							},
							'.MuiTypography-p__card__date': {
								marginBottom: remCalc(10),
							},
							'.card__topics__container': {
								display: 'flex',
								gap: remCalc(5),
								marginTop: remCalc(18),
								flexFlow: 'wrap',
								flex: 0,
							},
							'.card__content__container': {
								display: 'flex',
								flexDirection: 'row',
								gap: remCalc(24),
								'.card__content__container__top': {
									flex: '1',
								},
								'.roundImage': {
									width: remCalc(70),
									height: remCalc(70),
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									backgroundRepeat: 'none',
									flex: '0 auto',
									borderRadius: '100%',
									[theme.breakpoints.up('md')]: {
										width: remCalc(168),
										height: remCalc(168),
									},
								}
							}
						}
					}
				},
				{
					props: { variant: 'officeCard' },
					style: {
						backgroundColor: getColor('gray', 600),
						borderRadius: 11,
						height: '100%',
						transition: `all ${globalTransitionDuration} ease-in-out`, 
						[theme.breakpoints.down('md')]: {
							height: '300px',
							marginBottom: "1.25rem"
						},
						'&:hover': {
							borderRadius: '11px 64px'
						},
						'.MuiCardActionArea-root': {
							height: '100%',
							borderRadius: 0,
							textAlign: 'center',
							color: getColor('blue',700),
							transition: `all ${globalTransitionDuration} ease-in-out`,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							backgroundColor: getColor('gray', 600),
							'&:hover': {
								backgroundColor: getColor('blue', 500),
							},
							'icon': {
								marginBottom: remCalc(32),
								[theme.breakpoints.down('md')]: {
									marginBottom: remCalc(24),
								},
							},
							'.MuiCardContent-root': {
								height: '100%',
								borderRadius: 11,
								padding: '44px 90px 60px 90px',
								[theme.breakpoints.down('md')]: {
									padding: '30px 40px 40px 40px',
								},
								textAlign: 'center',
								color: getColor('blue',700),
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexDirection: 'column',
								'&.hasIcon': {
									padding: '30px',
								},
							},
							'.office': {
								color: colors.white,
								fontFamily: [
									'din-2014',
									'sans-serif',
								].join(', '),
								fontSize: 40,
								fontStyle: 'normal',
								fontWeight: 600,
								lineHeight: "46px",
								[theme.breakpoints.down('md')]: {
									fontSize: 28,
									marginBottom: remCalc(16),
								},
								marginBottom: remCalc(16),
								textAlign: "center"
							},
							'.address': {
								color: colors.white,
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
								[theme.breakpoints.down('md')]: {
									fontSize: 18,
									lineHeight: "22px"
								},
								fontSize: 20,
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: remCalc(24),
								textAlign: "center"

							},
							'.phone': {
								color: colors.white,
								fontFamily: [
									'open-sans',
									'sans-serif',
								].join(', '),
								fontSize: 20,
								[theme.breakpoints.down('md')]: {
									fontSize: 18,
									lineHeight: "22px"
								},
								fontStyle: 'normal',
								fontWeight: 400,
								lineHeight: remCalc(24),
								textAlign: "center"
							},
							'.card__topics__container': {
								display: 'flex',
								gap: remCalc(5),
								marginBottom: remCalc(18),
								flexFlow: 'wrap'
							}
						}
					}
				},
				{
					props: { variant: 'video', position: 'center' },
					style: {
						height: '100%',
						width: '100%',
						overflow: 'hidden',
						position: 'relative',
						cursor: "pointer",

						'& .MuiCardMedia-root': {
							height: '550px',
							[theme.breakpoints.down('md')]: {
								height: '250px',
							},
						},
						'& .MuiCardMedia-media': {
							height: '100%',
							width: '100%',
							objectFit: 'cover',
							[theme.breakpoints.down('md')]: {
								objectFit: 'contain',
							},
						},
						'.MuiBox-root': {
								position: "absolute",
								bottom: "10px",
								right: "10px",
							'&.topRight': {
									top: "10px",
									right: "10px",
									bottom: 'initial'
							}
						}
					}
				},
				{
					props: { variant: 'video', position: 'left' },
					style: {
						height: '100%',
						width: '100%',
						overflow: 'hidden',
						position: 'relative',
						cursor: "pointer",
						'& .MuiCardMedia-root': {
							height: '600px',
							[theme.breakpoints.down('md')]: {
								height: '375px',
							},
						},
						'& .MuiCardMedia-media': {
							width: '100%',
							objectFit: 'cover',
							[theme.breakpoints.down('md')]: {
								objectFit: 'cover',
							},
						},
						'.MuiBox-root': {
								position: "absolute",
								bottom: "10px",
								right: "10px",
						}
					}
				},
				{
					props: { variant: 'departmentJobsCard' },
					style: {
						display: "grid",
						gridTemplateColumns: "repeat(12, 1fr)",
						width: "100%",
						padding: "40px",
						[theme.breakpoints.down('md')]: {
							padding: "25px 20px",
							gridTemplateRows: "repeat(1, 1fr)",
						},
						borderRadius: 16,
						backgroundColor: getColor('gray', 600),
						transition: `all ${globalTransitionDuration} ease-in-out`,
						'&:hover': {
							backgroundColor: getColor('blue', 500),
						},
						".departmentJobsCard__header": {
							display: "flex",
							paddingRight: "30px",
							h2: {
								marginBottom: '25px'
							},
							p: {
								fontSize: 20,
								fontStyle: "normal", 
								fontWeight: 400, 
								lineHeight: "24px",
							},
							fontFamily: [
								"open-sans",
								"sans-serif",
							].join(', '),
							gridArea: "1 / 1 / 2 / 7",
							[theme.breakpoints.down('md')]: {
								gridArea:  "1 / 1 / 2 / 11",
								paddingRight: 0,
							}
						},
						".departmentJobsCard__jobs": {
							marginBottom: "auto",
							gridArea: "1 / 7 / 2 / 11",
							height: "100%",
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-start",
							[theme.breakpoints.down('md')]: {
								display: "block",
								gridArea:  "2 / 1 / 3 / 11"
							}
						},
						".departmentJobsCard__jobs__job": {
							a: {
								color: colors.white,
                                fontFamily: [
                                    "open-sans",
                                    "sans-serif",
                                ].join(', '),
								fontSize: "24px",
								fontStyle: "normal",
								fontWeight: 400,
								lineHeight: 1.25,

								textDecorationLine: "underline",
								textDecorationStyle: "solid",
								textDecorationSkipInk: "none",
								textDecorationThickness: "auto",
								textUnderlineOffset: "auto",
								textUnderlinePosition: "from-font",

								marginBottom: 25,
								display: "block",
							}
						},
						".departmentJobsCard__icon": {
							textAlign: "right",
							margin: "auto",
							gridArea: "1 / 11 / 2 / 13",
							[theme.breakpoints.down('md')]: {
								img: {
									width: "42px",
								},
								margin: 0,
								marginBottom: "auto",
								gridArea:  "1 / 11 / 3 / 13"
							}
						}
					}
				}
			],
		},
		MuiContainer: {
			styleOverrides: {
				root: {
					[theme.breakpoints.down('sm')]: { // Apply only for small screens (mobile)
						paddingLeft: '24px',  // Adjust as needed
						paddingRight: '24px',
					},
					'&.blog__post__masthead__content': {
						'.blog__post__masthead__topics__container': {
							display: 'flex',
							gap: remCalc(5),
							marginBottom: remCalc(18),
							flexFlow: 'wrap',
						}
					},
					'&.blog__post__content': {
						paddingTop: remCalc(0), 
						'a:not(.MuiButtonBase-root)': {
							color: colors.white,
						}
					},
					'&.content': {
						position: 'relative',
						'&.margin': {
							marginTop: remCalc(20),
							[theme.breakpoints.up('ms')]: {
								marginTop: remCalc(60),
							}
						}
					},
					'&.header__container': {
						padding: remCalc(10),
						[theme.breakpoints.up('ms')]: {
							// backgroundColor: getColor('blue', 600),
							backgroundColor: 'rgba(4, 81, 114, .95)',
							padding: '22px 54px 23px 54px',
						},
						'.logo': {
							[theme.breakpoints.down('ms')]: {
								backgroundColor: getColor('blue', 600),
								padding: '7.25px 6.59px 6.59px 6.59px',
								borderTopRightRadius: remCalc(16),
								borderBottomLeftRadius: remCalc(16),
								marginTop: '-7.25px',
								width: remCalc(56),
								height: remCalc(56),
							},
							'.logoImageSmall': {
								display: 'block',
								[theme.breakpoints.up('ms')]: {
									display: 'none',
								}
							},
							'.logoImageLarge': {
								display: 'none',
								[theme.breakpoints.up('ms')]: {
									display: 'block',
								}
							},
						},
						'.header__nav': {
							fontSize: 0,
							[theme.breakpoints.down('ms')]: {
								display: 'none',
							},
							ul: {
								display: 'flex',
								listStyle: 'none',
								justifyContent: 'flex-end',
								gap: remCalc(54),
								li: {
									display: 'block',
									a: {
										fontSize: remCalc(20),
										textDecoration: 'none',
										padding: '31px 0',
										color: colors.white,
										transition: `all ${globalTransitionDuration}`,
										borderBottom: `6px solid transparent`,
									},
									'&:nth-of-type(1) a': {
										'&:hover, &.active': {
											borderColor: getColor('yellow', 600),
										},
									},
									'&:nth-of-type(2) a': {
										'&:hover, &.active': {
											borderColor: getColor('red', 500),
										},
									},
									'&:nth-of-type(3) a': {
										position: 'relative',
										'&:hover, &.active': {
											borderColor: getColor('blue', 300),
										},
									},
									'&:nth-of-type(4) a': {
										'&:hover, &.active': {
											borderColor: getColor('blue', 200),
										},
									},
									'&:nth-of-type(n+5) a': {
										'&:hover, &.active': {
											borderColor: getColor('blue', 200),
										},
									},
								}
							},
						},
						'.navMenuButton': {
							display: 'block',
							[theme.breakpoints.up('ms')]: {
								display: 'none',
							},
							padding: 0,
						},
						'&.menu-is-open': {
							'.logo': {
								[theme.breakpoints.down('ms')]: {
									backgroundColor: getColor('blue', 700),
								},
							},

						},
					},
					'&.contact_page__social_media': {
						'h3': {
							fontFamily: [
								'franklin-gothic-atf',
								'sans-serif',
							].join(', '),
							fontSize: remCalc(48),
							fontWeight: 600,
							lineHeight: 1,
							// marginBottom: remCalc(60),
						},
						'h4': {
							fontFamily: [
								'din-2014',
								'sans-serif',
							].join(', '),
							fontSize: remCalc(40),
							fontWeight: 600,
							lineHeight: remCalc(46),
							marginBottom: remCalc(32),
						},
						'.contact_page__social_media__icons_container': {
							alignItems: 'center',
							justifyContent: 'center',
							display: 'flex',
							gap: remCalc(60),
							[theme.breakpoints.down('md')]: {
								gap: remCalc(40),
							},
							[theme.breakpoints.down('sm')]: {
								gap: remCalc(0),
							},
							padding: '0 16px',
							'.MuiGrid2-root': {
								width: remCalc(100),
								alignItems: 'center',
								justifyContent: 'center',
								display: 'flex',
								'a': {
									[theme.breakpoints.up('lg')]: {
										'&:hover': {
											'img': {
												filter: 'brightness(0) saturate(100%) invert(72%) sepia(80%) saturate(342%) hue-rotate(341deg) brightness(104%) contrast(104%)',
											}
										}
									}
								},
								[theme.breakpoints.down('md')]: {
									'&:nth-of-type(2)': {
										'img': {
											marginLeft: remCalc(25),
										}
									}
								},
								[theme.breakpoints.down('sm')]: {
									width: remCalc(84),
								},
							}
						}
					},
					'&.brand__container': {
						paddingTop: "150px",
						paddingBottom: "50px",
						[theme.breakpoints.down('md')]: {
							paddingTop: "32px",
						}
					},
					'&.SmartTag-root': {
						left: '50%',
						position: 'relative',
						transform: 'translateX(-50%)',
						width: '100vw',
						maxWidth: 'none',
						padding: '0',
						margin: '0',
						'.SmartTag-root': {
							left: 'unset',
							transform: 'unset',
							width: 'unset',
							maxWidth: 'unset',
						}
					},
					'&.ContentBlock-root': {
						[theme.breakpoints.down('sm')]: {
							paddingTop: "32px",
						}
					}
				}
			}
		},
		// MuiBox: {
		// 	styleOverrides: {
		// 		root: {
		// 			'&.blog__post__masthead': {
		// 				minHeight: remCalc(606),
		// 			},
		// 		}
		// 	}
		// },
		MuiModal: {
			styleOverrides: {
				root: {
					zIndex: 20000,
					'&.video-modal': {
						zIndex: 50000,
					}
				},
			}
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					'&.filter__content__accordion': {
						backgroundColor: 'transparent',
						width: '100%',
						'.MuiAccordionSummary-root': {
							minHeight: 'unset',
						},
						'.MuiAccordionDetails-root': {
							'.filters__list__wrap': {
								display: 'block',
								maxWidth: remCalc(700),
								paddingTop: remCalc(28),
								paddingBottom: remCalc(20),
								[theme.breakpoints.down('sm')]: {
									maxWidth: remCalc(250),
								},
								'.filters__list__wrap__inner': {
									display: 'flex',
									flexDirection: 'row',
									gap: remCalc(10),
									flexWrap: 'wrap',
								}
							}
						}
					}
				}
			}
		},
		MuiAccordionSummary: {
			styleOverrides: {
				content: {
					'&.Mui-expanded': {
						margin: '12px 0',
					}
				}
			}
		},
		MuiChip: {
			variants: [
				{
					props: { variant: 'filter' },
					style: {
						backgroundColor: getColor('blue',700),
						color: getColor('white',100),
						fontSize: remCalc(14),
						fontStyle: 'normal',
						fontWeight: 600,
						fontFamily: [
							'din-2014',
							'sans-serif',
						].join(', '),
						lineHeight: remCalc(16.3),
						borderRadius: 4,
						padding: '5px 10px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: remCalc(4.7),
						border: 'solid 1px',
						borderColor: getColor('blue', 200),
						'.MuiChip-label': {
							paddingLeft: 0,
						},
						'.MuiChip-deleteIcon': {
							marginRight: 0,
						}
					}
				},
			]
		},
		MuiListItemButton: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						backgroundColor: getColor('blue',500),
						'&:hover': {
							backgroundColor: getColor('gray',600),
						}
					},
					'&:hover': {
						backgroundColor: getColor('gray',600),
					}
				}
			}
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					'.MuiOutlinedInput-root': {
						borderRadius: 0,
						'& fieldset': { border: 'none' },
						'&:hover fieldset': { border: 'none' },
						'&.Mui-focused fieldset': { border: 'none' },
						borderBottom: '2px solid white',
					},
					// Adjust the input padding so text is closer to the bottom border
					'.MuiOutlinedInput-input': {
						color: 'white',
						paddingBottom: '4px', // Adjust this value as needed
						paddingLeft: '0',
						'&:is(:-webkit-autofill, :autofill)': {
							WebkitBackgroundClip: 'text !important',
							WebkitTextFillColor: 'white',
							background: 'transparent',
							color: 'white',
						},
					},
					// Label styling
					'.MuiInputLabel-root': {
						color: 'white',
						marginLeft: "-0.75rem"
					},
					'.MuiInputLabel-root.Mui-focused, .MuiInputLabel-root.Mui-error, .MuiFormLabel-asterisk': {
						color: 'white',
					},
					'.MuiFormHelperText-root': {
						color: 'white',
						marginLeft: 0,
						marginRight: 0,
						fontSize: remCalc(13),
					},
				}
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					'&.content__table': {
						'table': {
							backgroundColor: getColor('blue',700),
							borderCollapse: 'separate',
							borderSpacing: '2px',
							borderColor: getColor('blue',700),
							'thead': {
								backgroundColor: getColor('blue',200),
								color: getColor('blue',700),
								'tr': {
									border: 'none',
									'th': {
										border: 'none',
										color: getColor('blue',700),
										textAlign: 'left',
										padding: '1rem',
										fontSize: remCalc(20),
									}
								}
							},
							'tbody': {
								backgroundColor: getColor('blue',600),
								'tr': {
									border: 'none',
									'td': {
										border: 'none',
										color: colors.white,
										textAlign: 'left',
										padding: '1rem',
										fontSize: remCalc(20),
									}
								}
							}
						}
					}
				}
			}
		},
		MuiZBox: {
			styleOverrides: {
				root: {
					'&.content-404': {
						paddingTop: '265px',
						paddingBottom: '350px',
						background: `url(${background404})`,
				        backgroundSize: '1500px',
				        backgroundPosition: 'top 100px center',
						backgroundRepeat: 'no-repeat',
						position: 'relative',
						[theme.breakpoints.down('sm')]: {
							paddingTop: '280px',
							backgroundSize: '1000px',
							backgroundPosition: 'top 80px left 0px',
						},
						'.content-404__container': {
							position: 'relative',
							zIndex: '3',
							'p': {
								fontSize: remCalc(24),
								lineHeight: remCalc(28),
								marginTop: remCalc(10),
							},
							'.content-404__buttons': {
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'flex-start',
								alignItems: 'flex-start',
								gap: remCalc(16),
								[theme.breakpoints.down('sm')]: {
									marginTop: remCalc(16),
									'.MuiButton-root': {
										width: '100%',
									}
								},
							}
						},
						'.content-404__background': {
							position: 'absolute',
							top: '0',
							left: '0',
							width: '100%',
							height: '100%',
							overflow: 'hidden',
							'&:after': {
								content: '""',
								display: 'block',
								backgroundImage: `url(${blackhole})`,
								backgroundRepeat: 'no-repeat',
								backgroundSize: '2500px',
								backgroundPosition: 'top 100px center',
								position: 'absolute',
								top: '0',
								left: '50%',
								width: '125%',
								height: '100%',
								zIndex: '2',
								transform: 'translateX(-35%)',
								[theme.breakpoints.down('lg')]: {
									backgroundSize: '1800px',
									backgroundPosition: 'top 190px center',
									transform: 'translateX(-30%)',
									// backgroundPosition: 'top 50px left 0px',
								},
								[theme.breakpoints.down('md')]: {
									backgroundSize: '1650px',
									backgroundPosition: 'top 150px center',
									transform: 'translateX(-28%)',
									// backgroundPosition: 'top 50px left 0px',
								},
								[theme.breakpoints.down('sm')]: {
									backgroundSize: '800px',
									backgroundPosition: 'top 50px center',
									transform: 'translateX(-40%)',
									// backgroundPosition: 'top 50px left -150px',
								},
							},
						},
					},
					'&.single_location__directions_section': {
						paddingTop: remCalc(100),
						[theme.breakpoints.down('sm')]: {
							paddingTop: remCalc(31),
						},
						'.single_location__directions_section_container': {
							[theme.breakpoints.down('sm')]: {
								flexDirection: 'column-reverse',
								'.single_location__directions': {
									alignItems: 'flex-start',
									textAlign: 'left',
									'.MuiButton-root': {
										width: '100%',
									}
								}
							}
						}
					},
					'&.slider__title_holder': {
						display: 'flex',
						justifyContent: 'space-between',
					},
					'&.slider__arrows': {
						display: 'flex',
						flexDirection: 'row',
						gap: remCalc(10),
						alignItems: 'flex-end',
						justifyContent: 'center',
						[theme.breakpoints.down('md')]: {
							display: 'none',
						},
						'.swiper-button-prev-custom, .swiper-button-next-custom, .swiper-button-prev, .swiper-button-next': {
							all: 'unset',
							'svg': {
								all: 'unset',
							}
						},
						'.arrow': {
							backgroundColor: getColor('yellow', 600),
							width: remCalc(44),
							height: remCalc(44),
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							borderRadius: '100%',
							transition: `all ${globalTransitionDuration} ease-in-out`,
							'svg': {
								width: remCalc(9),
								height: remCalc(18),
							},
							'&.swiper-button-disabled': {
								backgroundColor: getColor('yellow', 600),
								opacity: '.5',
								'&:hover': {
									cursor: 'not-allowed',
								}
							},
							'&:hover': {
								backgroundColor: getColor('yellow', 700),
								cursor: 'pointer',
							}
						}
					},
					'&.astronautBG': {
						zIndex: '-1', 
						position: 'absolute', 
						width: '100%', 
						height: '100%', 
						left: '0', 
						top: '0',
						backgroundPosition: 'center',
						backgroundSize: 'contain',
						backgroundRepeat: 'no-repeat',
					}
				}
			}
		}
	},
}, componentStyles));

export default theme;